<script>
  import { ShieldAlert, Info, UserCheck, Clock } from 'lucide-svelte';
  import { _ } from '../lib/i18n.js';
  import SignatureStateBadge from './SignatureStateBadge.svelte';
  import { SIGNATURE_STATES, signatureStateOf, signatureUnverifiedReasons } from './signatureState.js';

  /** Stored signature info for one document, or null when none exists. */
  export let info = null;
  /**
   * Show operator-only detail (the verifier's own chain error text). Off by
   * default because that string can carry the server's trust-bundle path, and
   * this component is also rendered inside the customer portal.
   */
  export let showTechnicalDetail = false;

  $: state = signatureStateOf(info);
  $: reasons = signatureUnverifiedReasons(info);
  $: attestation = info?.attestation?.attested ? info.attestation : null;
  /**
   * A verdict that rests on a timestamp authority's word rather than on today's
   * date. Shown as a fact, not as a warning: a certificate that was valid when
   * it signed is not a problem, and rendering it as one would send the operator
   * looking for something to fix that is not there.
   */
  $: timestampRescued = info?.chainVerifiedAtSigningTime === true && info?.chainVerified === true;
  $: verifiedTimestamp = info?.timestamp?.verified === true ? info.timestamp : null;

  /**
   * @param {string|null|undefined} value ISO timestamp
   * @returns {string}
   */
  function formatDate(value) {
    if (!value) return '-';
    return new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  }
</script>

<div class="space-y-2">
  <div class="flex items-center flex-wrap gap-2">
    <SignatureStateBadge {state} />
    <span class="text-xs text-gray-500 dark:text-gray-400">
      {#if state === SIGNATURE_STATES.BOUND}
        {timestampRescued ? $_('signatureState.boundHintTimestamp') : $_('signatureState.boundHint')}
      {:else if state === SIGNATURE_STATES.MANUAL}
        {$_('signatureState.manualHint')}
      {:else if state === SIGNATURE_STATES.UNBOUND}
        {$_('signatureState.unboundHint')}
      {/if}
    </span>
  </div>

  <!-- The timestamp, when one verified. Neutral styling on purpose: this is the
       evidence that a certificate which has since expired was valid when it
       signed, and it must not read as something to worry about. -->
  {#if verifiedTimestamp}
    <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
      <div class="flex items-start">
        <Clock class="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
        <div class="text-xs text-blue-800 dark:text-blue-200 space-y-1">
          <p class="font-medium">{$_('signatureState.timestampTitle')}</p>
          <p>{$_('signatureState.timestampAt')}: {formatDate(verifiedTimestamp.time)}</p>
          {#if verifiedTimestamp.authority}
            <p>{$_('signatureState.timestampAuthority')}: {verifiedTimestamp.authority}</p>
          {/if}
          {#if timestampRescued}
            <p>{$_('signatureState.timestampRescued')}</p>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- What could not be verified, specifically. A single generic "attenzione"
       teaches the reader nothing and trains them to skip it. -->
  {#if reasons.length > 0}
    <div class="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
      <div class="flex items-start">
        <ShieldAlert class="h-4 w-4 text-orange-500 dark:text-orange-400 mt-0.5 mr-2 flex-shrink-0" />
        <div class="text-xs text-orange-800 dark:text-orange-200 space-y-1">
          <p class="font-medium">{$_('signatureState.reasonsTitle')}</p>
          <ul class="list-disc list-inside space-y-1">
            {#each reasons as reason}
              <li>{$_(`signatureState.reasons.${reason}`)}</li>
            {/each}
          </ul>
          {#if showTechnicalDetail && info?.chainError}
            <p class="mt-1 font-mono text-[11px] opacity-80">
              {$_('signatureState.technicalDetail')}: {info.chainError}
            </p>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- The manual path: an internal user vouched for a document that carries no
       digital signature at all. Their name is the only evidence it has. -->
  {#if state === SIGNATURE_STATES.MANUAL}
    <div class="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
      <div class="flex items-start">
        <Info class="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
        <div class="text-xs text-amber-800 dark:text-amber-200 space-y-1">
          <p>{$_('signatureState.notADigitalSignature')}</p>
          {#if info?.attestedByEmail || info?.attestedByUserId}
            <p>{$_('signatureState.manualBy')}: {info.attestedByEmail || info.attestedByUserId}</p>
          {/if}
          {#if info?.attestedAt}
            <p>{$_('signatureState.manualAt')}: {formatDate(info.attestedAt)}</p>
          {/if}
          {#if info?.notes}
            <p>{$_('signatureState.manualNotes')}: {info.notes}</p>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- An attestation already on record: who took responsibility, and when.
       A system-recorded one says so, because "attestato da Federico" would
       claim a person examined a document when what happened is that the
       gestionale signed it on their instruction. -->
  {#if attestation}
    <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
      <div class="flex items-start">
        <UserCheck class="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
        <div class="text-xs text-blue-800 dark:text-blue-200 space-y-1">
          <p class="font-medium">
            {attestation.source === 'countersign'
              ? $_('signatureState.countersignAttestedTitle')
              : $_('signatureState.attestedTitle')}
          </p>
          {#if attestation.source === 'countersign'}
            <p>{$_('signatureState.countersignAttestedBody')}</p>
            <p>{$_('signatureState.countersignRequestedBy')}: {attestation.byEmail || attestation.byUserId}</p>
          {:else}
            <p>{$_('signatureState.attestedBy')}: {attestation.byEmail || attestation.byUserId}</p>
          {/if}
          <p>{$_('signatureState.attestedAt')}: {formatDate(attestation.at)}</p>
          {#if attestation.notes}
            <p>{$_('signatureState.attestationNotes')}: {attestation.notes}</p>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>
